import { Component } from '@theme/component';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

const GIFT_WRAP_HANDLE = 'wrap-as-a-gift';

/**
 * @returns {string[]}
 */
function getCartSectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) {
      ids.add(el.dataset.sectionId);
    }
  });
  return Array.from(ids);
}

/**
 * @returns {Promise<Object>}
 */
async function fetchCart() {
  const response = await fetch('/cart.js');
  if (!response.ok) throw new Error('Failed to fetch cart');
  return response.json();
}

/**
 * @param {Object} cart
 * @param {string} variantId
 * @returns {string | null}
 */
function findGiftWrapKey(cart, variantId) {
  const item = cart.items.find(
    (line) => String(line.variant_id) === String(variantId) && line.handle === GIFT_WRAP_HANDLE
  );
  return item ? item.key : null;
}

/**
 * @param {number} quantity
 * @param {string} variantId
 * @returns {Promise<boolean>}
 */
async function updateGiftWrap(quantity, variantId) {
  if (!variantId) return false;

  const sectionIds = getCartSectionIds();
  const sections = sectionIds.join(',');
  const sectionsUrl = window.location.pathname;

  const currentCart = await fetchCart();
  const lineItemKey = findGiftWrapKey(currentCart, variantId);

  const deferredPromise = CartLinesUpdateEvent.createPromise();

  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: quantity === 1 ? 'add' : 'remove',
      context: 'cart',
      lines: [{ id: lineItemKey || String(variantId), quantity }],
      promise: deferredPromise.promise,
    })
  );

  try {
    let response;
    let data;

    if (quantity === 1) {
      if (lineItemKey) {
        const cart = await fetchCart();
        deferredPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { source: 'gift-wrap', didError: false },
        });
        return true;
      }

      response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          sections,
          sections_url: sectionsUrl,
        }),
      });
      data = await response.json();
    } else if (quantity === 0) {
      if (!lineItemKey) {
        const cart = await fetchCart();
        deferredPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { source: 'gift-wrap', didError: false },
        });
        return true;
      }

      response = await fetch(Theme.routes.cart_change_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          id: lineItemKey,
          quantity: 0,
          sections,
          sections_url: sectionsUrl,
        }),
      });
      data = JSON.parse(await response.text());
    } else {
      return false;
    }

    if (data.status || data.errors) {
      const message = data.description || data.message || 'Gift wrap update failed';
      deferredPromise.reject(new Error(message));
      console.error('[gift-wrap]', message);
      return false;
    }

    const cart = await fetchCart();

    deferredPromise.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        sections: data.sections,
        source: 'gift-wrap',
        didError: false,
      },
    });

    return true;
  } catch (error) {
    deferredPromise.reject(error);
    console.error('[gift-wrap] update failed:', error);
    return false;
  }
}

/**
 * Sync checkbox with actual cart state.
 */
async function syncGiftWrapCheckbox() {
  const checkbox = document.getElementById('gift-wrap-checkbox');
  if (!(checkbox instanceof HTMLInputElement)) return;

  const variantId = checkbox.dataset.giftWrapId;
  if (!variantId) return;

  try {
    const cart = await fetchCart();
    checkbox.checked = findGiftWrapKey(cart, variantId) !== null;
  } catch (error) {
    console.error('[gift-wrap] failed to sync checkbox:', error);
  }
}

/**
 * Event delegation — survives cart drawer re-renders.
 * @param {Event} event
 */
async function handleGiftWrapChange(event) {
  const target = event.target;
  if (!(target instanceof HTMLInputElement)) return;
  if (target.id !== 'gift-wrap-checkbox') return;
  if (target.dataset.giftWrapBusy === 'true') return;

  const variantId = target.dataset.giftWrapId;
  if (!variantId) return;

  const container = target.closest('#gift-wrap-container');
  const spinner = container?.querySelector('.loading-spinner');
  const priceSpan = container?.querySelector('[data-gift-wrap-price]');
  const isChecked = target.checked;

  target.dataset.giftWrapBusy = 'true';
  target.disabled = true;
  spinner?.classList.remove('hidden');
  priceSpan?.classList.add('hidden');

  const success = await updateGiftWrap(isChecked ? 1 : 0, variantId);

  target.disabled = false;
  target.dataset.giftWrapBusy = 'false';
  spinner?.classList.add('hidden');
  priceSpan?.classList.remove('hidden');

  if (!success) {
    target.checked = !isChecked;
  } else {
    await syncGiftWrapCheckbox();
  }
}

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    document.addEventListener('change', handleGiftWrapChange);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);

    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }

    syncGiftWrapCheckbox();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    document.removeEventListener('change', handleGiftWrapChange);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
  }

  #handleDrawerOpen = () => {
    this.#updateStickyState();
    syncGiftWrapCheckbox();

    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () => {
          requestAnimationFrame(() => {
            this.#updateStickyState();
            syncGiftWrapCheckbox();
          });
        };

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
